// components/DuplicateDrawer.tsx
import React from 'react';
import { Drawer, Box, Typography, List, ListItem, ListItemText, Button, Fab, Tooltip } from '@mui/material';
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { NAVIGATION_CONFIG } from '../config/api';

interface DuplicateDrawerProps {
  open: boolean;
  duplicateMatches: any[];
  checkingDuplicates: boolean;
  drawerWidth: number;
  drawerMinimized: boolean;
  customerToken?: string | null;
  threshold: number;
  onMinimize: () => void;
  onRestore: () => void;
  onSelectCustomer?: (customer: any) => void;
}

const MAX_MATCHING_SCORE = 100;

export const DuplicateDrawer: React.FC<DuplicateDrawerProps> = ({
  open,
  duplicateMatches,
  checkingDuplicates,
  drawerWidth,
  drawerMinimized,
  customerToken,
  threshold,
  onMinimize,
  onRestore,
  onSelectCustomer,
}) => {
  return (
    <>
      <Drawer
        anchor="right"
        variant="persistent"
        open={open}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexShrink: 0,
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        <Box sx={{ width: 400, p: 3 }}>
          <Typography variant="h6" color="warning.main" sx={{ mb: 2 }}>
            {checkingDuplicates
              ? "Checking for possible duplicates..."
              : "Possible duplicate customers found:"}
          </Typography>
          {checkingDuplicates ? (
            <Typography variant="body2">Checking...</Typography>
          ) : duplicateMatches.length > 0 ? (
            <List dense>
              {duplicateMatches.map((match, idx) => {
                const dob = match.dateOfBirth
                  ? match.dateOfBirth.split("T")[0]
                  : "";
                return (
                  <ListItem
                    key={idx}
                    divider={idx < duplicateMatches.length - 1}
                    sx={{
                      background:
                        match.score === MAX_MATCHING_SCORE
                          ? "#FFEB3B"
                          : match.score >= threshold
                          ? "#FFF9C4"
                          : "inherit",
                    }}
                  >
                    <ListItemText
                      primary={
                        <span>
                          <b>
                            {match.firstName} {match.lastName}
                          </b>{" "}
                          (Score: {match.score}%)
                        </span>
                      }
                      secondary={
                        <>
                          <span>
                            DOB: {dob} | Email: {match.email || "-"} | Phone:{" "}
                            {match.primaryPhone || "-"} | Postcode:{" "}
                            {match.primaryPostcode || "-"}
                          </span>
                          <br />
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                              size="small"
                              onClick={() => {
                                const customerUrl = `${window.location.origin}${NAVIGATION_CONFIG.getCustomerDetailRoute(match.customerExternalId)}`;
                                window.open(
                                  customerUrl,
                                  "_blank",
                                  "noopener,noreferrer"
                                );
                              }}
                            >
                              View Customer
                            </Button>
                            {customerToken && onSelectCustomer && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => onSelectCustomer(match)}
                              >
                                Select Customer
                              </Button>
                            )}
                          </Box>
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography variant="body2" sx={{ mb: 2 }}>
              No duplicate customers found.
            </Typography>
          )}
          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Button onClick={onMinimize}>Minimize</Button>
          </Box>
        </Box>
      </Drawer>

      {drawerMinimized && (
        <Box
          sx={{
            position: "fixed",
            right: 32,
            bottom: 32,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }}
        >
          <Tooltip title="Show possible duplicates" placement="left">
            <Fab
              color="warning"
              onClick={onRestore}
              aria-label="Show Duplicates"
            >
              <WarningAmberIcon />
            </Fab>
          </Tooltip>
        </Box>
      )}
    </>
  );
};